import type {
  InboxChannelPreference,
  InboxEventType,
  InboxListResponse,
  InboxNotification,
  InboxPreferencesPutBody,
  InboxPreferencesResponse,
  InboxPushDelivery,
} from "@evolua/api-client";

const LABELS: Record<InboxEventType, string> = {
  "vacation.requested": "Férias solicitadas",
  "vacation.approved": "Férias aprovadas",
  "vacation.rejected": "Férias reprovadas",
  "payroll.available": "Holerite disponível",
  "security.alert": "Alerta de segurança",
  "lgpd.export_ready": "Exportação LGPD pronta",
  "request.updated": "Atualização de solicitação",
  "announcement.published": "Novo comunicado",
  "integration.error": "Erro de integração",
  "admin.alert": "Alerta administrativo",
};

const DEFAULT_PREFS: Record<InboxEventType, InboxChannelPreference> = {
  "vacation.requested": { in_app: true, push: true, email: true },
  "vacation.approved": { in_app: true, push: true, email: true },
  "vacation.rejected": { in_app: true, push: true, email: true },
  "payroll.available": { in_app: true, push: true, email: true },
  "security.alert": { in_app: true, push: true, email: true },
  "lgpd.export_ready": { in_app: true, push: true, email: true },
  "request.updated": { in_app: true, push: false, email: true },
  "announcement.published": { in_app: true, push: true, email: false },
  "integration.error": { in_app: true, push: false, email: true },
  "admin.alert": { in_app: true, push: false, email: true },
};

const notifications: InboxNotification[] = [
  {
    id: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa",
    type: "payroll.available",
    title: "Holerite disponível",
    body: "Seu holerite de julho/2026 já está disponível para consulta.",
    href: "/holerite",
    createdAt: "2026-08-10T14:00:00.000Z",
    readAt: null,
    pushQueued: true,
  },
  {
    id: "bbbbbbbb-bbbb-4bbb-8bbb-bbbbbbbbbbbb",
    type: "vacation.approved",
    title: "Férias aprovadas",
    body: "Sua solicitação de férias foi aprovada.",
    href: "/ferias",
    createdAt: "2026-08-09T10:00:00.000Z",
    readAt: "2026-08-09T11:00:00.000Z",
    pushQueued: false,
  },
];

const preferences: Record<InboxEventType, InboxChannelPreference> = {
  "vacation.requested": { ...DEFAULT_PREFS["vacation.requested"] },
  "vacation.approved": { ...DEFAULT_PREFS["vacation.approved"] },
  "vacation.rejected": { ...DEFAULT_PREFS["vacation.rejected"] },
  "payroll.available": { ...DEFAULT_PREFS["payroll.available"] },
  "security.alert": { ...DEFAULT_PREFS["security.alert"] },
  "lgpd.export_ready": { ...DEFAULT_PREFS["lgpd.export_ready"] },
  "request.updated": { ...DEFAULT_PREFS["request.updated"] },
  "announcement.published": { ...DEFAULT_PREFS["announcement.published"] },
  "integration.error": { ...DEFAULT_PREFS["integration.error"] },
  "admin.alert": { ...DEFAULT_PREFS["admin.alert"] },
};

const pushDeliveries: InboxPushDelivery[] = [
  {
    id: "cccccccc-cccc-4ccc-8ccc-cccccccccccc",
    toSubject: "demo",
    notificationId: "aaaaaaaa-aaaa-4aaa-8aaa-aaaaaaaaaaaa",
    at: "2026-08-10T14:00:01.000Z",
    status: "queued",
  },
];

function unreadCount(): number {
  return notifications.filter((n) => n.readAt === null).length;
}

export function listDemoInbox(filter?: {
  unreadOnly?: boolean;
  type?: InboxEventType;
}): InboxListResponse {
  let data = notifications.map((n) => ({ ...n }));
  if (filter?.unreadOnly) data = data.filter((n) => !n.readAt);
  if (filter?.type) data = data.filter((n) => n.type === filter.type);
  data.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  return {
    data,
    total: data.length,
    unreadCount: unreadCount(),
  };
}

export function getDemoInboxUnreadCount(): { unreadCount: number } {
  return { unreadCount: unreadCount() };
}

export function markDemoInboxRead(id: string): InboxNotification {
  const item = notifications.find((n) => n.id === id);
  if (!item) throw new Error("Notificação não encontrada");
  if (!item.readAt) item.readAt = new Date().toISOString();
  return { ...item };
}

export function markAllDemoInboxRead(): { marked: number } {
  let marked = 0;
  const now = new Date().toISOString();
  for (const n of notifications) {
    if (!n.readAt) {
      n.readAt = now;
      marked += 1;
    }
  }
  return { marked };
}

export function getDemoInboxPreferences(): InboxPreferencesResponse {
  return { preferences: { ...preferences }, labels: { ...LABELS } };
}

export function putDemoInboxPreferences(
  body: InboxPreferencesPutBody,
): InboxPreferencesResponse {
  for (const [type, patch] of Object.entries(body.preferences ?? {})) {
    const key = type as InboxEventType;
    if (!preferences[key] || !patch) continue;
    if (key === "security.alert") {
      preferences[key] = {
        in_app: true,
        email: true,
        push: patch.push ?? preferences[key].push,
      };
    } else {
      preferences[key] = {
        in_app: patch.in_app ?? preferences[key].in_app,
        push: patch.push ?? preferences[key].push,
        email: patch.email ?? preferences[key].email,
      };
    }
  }
  return getDemoInboxPreferences();
}

export function listDemoPushDeliveries(): { data: InboxPushDelivery[] } {
  return { data: pushDeliveries.map((d) => ({ ...d })) };
}
